import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { useSolana } from '../context/WalletContext';
import { toast } from 'sonner';
import axios from 'axios';
import bs58 from 'bs58';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginModalContext = createContext(null);
export const useLoginModal = () => useContext(LoginModalContext);

export const LoginModalProvider = ({ children }) => {
  const { t } = useTranslation();
  const { setAuth, user } = useAuth();
  const { settings: siteSettings } = useSite();
  const { connected, publicKey, signMessage, disconnect, wallet, wallets, select, connect, connecting } = useSolana();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('connect'); // connect | signing | success | error | deactivated
  const [hasAttempted, setHasAttempted] = useState(false);
  const [deactivationMessage, setDeactivationMessage] = useState(null);
  const [pendingWallet, setPendingWallet] = useState(null);
  const inProgress = useRef(false);

  const openLogin = useCallback(() => {
    setError(null);
    setStep('connect');
    setHasAttempted(false);
    inProgress.current = false;
    setOpen(true);
  }, []);

  // Close modal once logged in
  useEffect(() => {
    if (user && open) setOpen(false);
  }, [user, open]);

  const handleWalletLogin = useCallback(async () => {
    if (!publicKey || inProgress.current) return;
    inProgress.current = true;
    setLoading(true);
    setStep('signing');
    setError(null);
    setHasAttempted(true);
    setDeactivationMessage(null);

    try {
      const walletAddress = publicKey.toBase58();
      const statusRes = await axios.get(`${API}/auth/check-account-status?wallet_address=${walletAddress}`);
      if (statusRes.data.exists && !statusRes.data.is_active) {
        setStep('deactivated');
        setDeactivationMessage(statusRes.data.message || 'Your account has been deactivated by admin');
        setLoading(false);
        inProgress.current = false;
        disconnect();
        return;
      }

      const nonceRes = await axios.post(`${API}/auth/wallet/nonce`, { wallet_address: walletAddress });
      const { nonce, message } = nonceRes.data;
      const encodedMessage = new TextEncoder().encode(message);
      const signedMessage = await signMessage(encodedMessage);
      const signature = bs58.encode(signedMessage);

      const loginRes = await axios.post(`${API}/auth/wallet/login`, {
        wallet_address: walletAddress,
        signature,
        nonce
      });

      const { access_token, user: userData } = loginRes.data;
      setAuth(access_token, userData);
      setStep('success');
      toast.success(t('messages.welcome', { defaultValue: 'Welcome!' }));
      setTimeout(() => setOpen(false), 900);
    } catch (err) {
      console.error('Wallet login error:', err);
      inProgress.current = false;
      if (err.response?.status === 403 && err.response?.data?.detail?.code === 'ACCOUNT_DEACTIVATED') {
        setStep('deactivated');
        setDeactivationMessage(err.response.data.detail.message || 'Your account has been deactivated');
        disconnect();
        return;
      }
      setStep('error');
      if (err.message?.includes('User rejected')) {
        setError(t('auth.signatureRejected', { defaultValue: 'You rejected the signature request.' }));
      } else if (typeof err.response?.data?.detail === 'string') {
        setError(err.response.data.detail);
      } else {
        setError(t('auth.verifyFailed', { defaultValue: 'Failed to verify wallet. Please try again.' }));
      }
    } finally {
      setLoading(false);
      inProgress.current = false;
    }
  }, [publicKey, signMessage, disconnect, setAuth, t]);

  // Auto-trigger login when wallet connects while modal open
  useEffect(() => {
    if (open && connected && publicKey && step === 'connect' && !hasAttempted && !inProgress.current) {
      handleWalletLogin();
    }
  }, [open, connected, publicKey, step, hasAttempted, handleWalletLogin]);

  // Select a wallet then connect it directly (no nested wallet-adapter modal)
  const handleSelectWallet = useCallback((walletName) => {
    setError(null);
    setStep('connect');
    setHasAttempted(false);
    if (wallet?.adapter?.name === walletName) {
      // Already selected — connect immediately
      connect().catch((err) => {
        if (!String(err?.message || '').includes('User rejected')) {
          setStep('error');
          setError(t('auth.connectFailed', { defaultValue: 'Failed to connect wallet. Make sure the extension is installed and unlocked.' }));
        }
      });
    } else {
      setPendingWallet(walletName);
      select(walletName);
    }
  }, [wallet, connect, select, t]);

  // Once the selected wallet is set, trigger connect
  useEffect(() => {
    if (open && pendingWallet && wallet?.adapter?.name === pendingWallet && !connected && !connecting) {
      setPendingWallet(null);
      connect().catch((err) => {
        if (!String(err?.message || '').includes('User rejected')) {
          setStep('error');
          setError(t('auth.connectFailed', { defaultValue: 'Failed to connect wallet. Make sure the extension is installed and unlocked.' }));
        }
      });
    }
  }, [open, pendingWallet, wallet, connected, connecting, connect, t]);

  const primaryColor = siteSettings?.colors?.primary || '#FF8C00';

  return (
    <LoginModalContext.Provider value={{ openLogin }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border border-[#FFC300]/25 max-w-sm p-7" data-testid="login-modal">
          <DialogTitle className="sr-only">Connect Your Wallet</DialogTitle>
          <div className="text-center mb-5">
            {siteSettings?.logo ? (
              <img src={siteSettings.logo} alt={siteSettings?.app_name || 'Cardtopia'} className="h-8 object-contain mx-auto mb-4" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFC300] to-[#FFC300] flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-8 h-8 text-white" />
              </div>
            )}
            <h2 className="text-xl font-black text-[#1A1206] font-['Unbounded']">Connect Your Wallet</h2>
            <p className="text-[#6E6452] text-sm mt-1">Connect your Solana wallet to continue</p>
          </div>

          {step === 'deactivated' ? (
            <motion.div className="text-center py-2" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-red-500/20">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
                <p className="text-red-300 text-sm">{deactivationMessage}</p>
              </div>
              <Button onClick={() => { setStep('connect'); setDeactivationMessage(null); setHasAttempted(false); }} variant="outline" className="border-[#FFC300]/40 text-[#6E6452]">
                Try Another Wallet
              </Button>
            </motion.div>
          ) : step === 'success' ? (
            <motion.div className="text-center py-6" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-[#FFFDF7]/25">
                <Wallet className="w-7 h-7 text-[#FF8C00]" />
              </div>
              <h3 className="text-lg font-bold text-white">Welcome!</h3>
              <p className="text-[#6E6452] text-sm">Signing you in...</p>
            </motion.div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-500/50 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {!connected ? (
                <div className="space-y-2.5" data-testid="login-modal-select-wallet">
                  {(wallets || []).map((w) => {
                    const isInstalled = w.readyState === 'Installed';
                    const isLoadable = w.readyState === 'Installed' || w.readyState === 'Loadable';
                    return (
                      <button
                        key={w.adapter.name}
                        onClick={() => handleSelectWallet(w.adapter.name)}
                        disabled={connecting}
                        className="w-full flex items-center gap-3 px-4 h-14 rounded-xl bg-[#FFF8EA] border border-[#F0E3C8] hover:border-[#FF8C00] hover:bg-[#FFF3D6] transition-colors disabled:opacity-50"
                        data-testid={`wallet-option-${w.adapter.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {w.adapter.icon && (
                          <img src={w.adapter.icon} alt={w.adapter.name} className="w-7 h-7 rounded-md" />
                        )}
                        <span className="flex-1 text-left text-[#1A1206] font-semibold">{w.adapter.name}</span>
                        {isInstalled ? (
                          <span className="text-[10px] font-bold text-[#FF8C00] bg-[#FFC300]/10 px-2 py-0.5 rounded-full">Detected</span>
                        ) : isLoadable ? (
                          <span className="text-[10px] text-[#6E6452]/60">Install</span>
                        ) : null}
                      </button>
                    );
                  })}
                  {(!wallets || wallets.length === 0) && (
                    <p className="text-center text-[#6E6452] text-sm py-4">No Solana wallets found. Please install Phantom or Solflare.</p>
                  )}
                </div>
              ) : step === 'error' || error ? (
                <Button
                  onClick={() => { setError(null); setStep('connect'); setHasAttempted(false); handleWalletLogin(); }}
                  className="w-full h-14 text-base font-bold rounded-xl bg-gradient-to-r from-[#FFD400] to-[#FF8C00] hover:from-[#FFC300] hover:to-[#FF6A00] text-[#1A1206]"
                  data-testid="login-modal-retry"
                >
                  {t('common.retry', { defaultValue: 'Try Again' })}
                </Button>
              ) : (
                <div className="flex flex-col items-center justify-center py-5 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FF8C00]" />
                  <p className="text-[#6E6452] text-sm">
                    {step === 'signing' ? 'Please sign in your wallet...' : 'Verifying...'}
                  </p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </LoginModalContext.Provider>
  );
};
